<template>
    <LoaderSplash :loaded="loaded">
        <b-container v-if="loaded">
            <b-row>
                <b-col>
                    <h1>
                        {{ event.name }}
                    </h1>

                    <b-tabs content-class="mt-3" class="mt-5">
                        <b-tab title="Invites">
                            <h2>
                                {{ invites.length }} invited
                            </h2>                

                            <b-button variant="outline-primary mt-3" :to="{ name: 'send-invite', params: { id: id } }">
                                Create Invite
                            </b-button>            
                            
                            <div class="mt-3">
                                <span class="mr-2">
                                    <i class="fa fa-check text-success"></i>    
                                    {{ invites_accepted }} accepted
                                </span>

                                <span class="mr-2">
                                    <i class="fa fa-times text-danger"></i>  
                                    {{ invites_rejected }} rejected 
                                </span>

                                <span class="mr-2">
                                    <i class="fa fa-eye text-primary"></i>  
                                    {{ invites_seen }} seen
                                </span>

                                <span class="mr-2">
                                    <i class="fa fa-paper-plane text-info"></i>  
                                    {{ invites_sent }} sent
                                </span>

                                <span>
                                    <i class="fa fa-pen text-muted"></i>  
                                    {{ invites_unsent }} unsent
                                </span>
                            </div>

                            <b-row>
                                <b-input-group class="col-md-6 mt-5">
                                    <b-form-input v-model="filter" placeholder="Type to search" class="mb-0" />

                                    <b-input-group-append>
                                        <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-row>

                            <b-table class="mt-3" 
                                striped 
                                hover
                                stacked="md"
                                :filter="filter"
                                :items="invites"
                                :fields="fields">

                                <template slot="actions" slot-scope="row">
                                    <b-button class="btn-info mr-2" :to="{ name: 'invite', params: { invite_id: row.item.id } }">
                                        Edit
                                    </b-button>

                                    <b-button class="btn-danger" @click="askDeleteInvite(row.item)">
                                        Delete
                                    </b-button>
                                </template>
                            </b-table>

                            <b-button class="btn-info mr-2" v-if="true" @click="sendPendingInvites()">
                                Send Unsent
                            </b-button>

                            <b-button class="btn-info mr-2" @click="resendUnconfirmedInvites()">
                                Resend Unconfirmed
                            </b-button>

                            <vue-csv-downloader
                                :data="csv_invites"
                                :fields="csv_fields"
                                :download-name="csv_filename">
                                <b-button class="btn-info">
                                    Download As CSV
                                </b-button>
                            </vue-csv-downloader>
                        </b-tab>

                        <b-tab title="Details">
                            <img class="w-100" v-bind:src="event.image">

                            <div class="my-3">
                                <h3>
                                    Description
                                </h3>

                                <div v-html="event.description"></div>
                            </div>

                            <div class="my-3">
                                <TimesBlock :event="event"/>
                            </div>
                            <LocationBlock :event="event"/>
                        </b-tab>

                        <b-tab title="Itinerary">
                            <itinerary-form :days="days" :event="event" :can-manage-days="false"></itinerary-form>
                        </b-tab>

                        <b-tab title="Updates">
                            <update-form :updates="updates" :event="event"></update-form>
                        </b-tab>
                    </b-tabs>

                    <b-modal 
                        v-model="show_delete_modal"
                        id="delete-modal"
                        title="Warning"
                        @ok="deleteInvite()"
                        header-bg-variant="danger"
                        header-text-variant="light"
                        ok-title="Delete"
                        ok-variant="danger">
                        Are you sure you want to permanently delete this invite?
                    </b-modal>
                </b-col>
            </b-row>
        </b-container>
    </LoaderSplash>
</template>

<script>
    import ItineraryForm from '@/components/ItineraryForm.vue';
    import UpdateForm from '@/components/UpdateForm.vue';
    import eventRepository from "@/repository/events";
    import inviteRepository from "@/repository/invites";
    import LocationBlock from "@/components/LocationBlock";
    import TimesBlock from "@/components/TimesBlock";
    import VueCsvDownloader from 'vue-csv-downloader';

    let date_format = "DD-MM-YYYY HH:mm";

    export default {
        components: {
            ItineraryForm,
            UpdateForm,
            LocationBlock,
            TimesBlock,
            VueCsvDownloader
        },

        props: ['id'],

        data() {
            return {
                loaded: false,
                event: null,
                invites: [],
                updates: [],
                days: [],
                itinerary_items: [],
                csv_fields: [
                    'first_name',
                    'last_name',
                    'phone',
                    'email',
                    'status'
                ],
                fields: [
                    {
                        key: 'first_name',
                        sortable: true
                    },
                    {
                        key: 'last_name',
                        sortable: true
                    },
                    {
                        key: 'phone',
                        sortable: true
                    },
                    {
                        key: 'email',
                        sortable: true
                    },
                    {
                        key: 'status',
                        sortable: true
                    },
                    'actions'
                ],
                show_delete_modal: false, 
                invite_to_delete: null,
                filter: ""
            }
        },

        computed: {
            csv_filename() {
                return this.event.name + " Invite List.csv";
            },

            csv_invites(){
              return this.invites.map(invite=>{
                  invite.answers.forEach(answer=>{
                      invite[answer.question.text] = answer.text;
                  });
                  return invite;
              })
            },

            invites_accepted() {
                return this.invites.filter(invite => invite.status == "ACCEPTED").length;
            },

            invites_rejected() {
                return this.invites.filter(invite => invite.status == "REJECTED").length;
            },

            invites_seen() {
                return this.invites.filter(invite => invite.status == "VIEWED").length;
            },

            invites_sent() {
                return this.invites.filter(invite => invite.status == "SENT").length;
            },

            invites_unsent() {
                return this.invites.filter(invite => invite.status == "UNSENT").length;
            },
        },

        methods: {
            askDeleteInvite(item) {
                this.show_delete_modal = true;
                this.invite_to_delete = item;
            },
            
            deleteInvite() {
                inviteRepository.destroy(this.invite_to_delete.id)
                    .then(response => {
                        let id = this.invites.findIndex(invite => invite.id == this.invite_to_delete.id);

                        this.invites.splice(id, 1);
                        
                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Invite deleted',
                            text: 'Invite successfully deleted.'
                        });
                    });
            },
            
            sendPendingInvites() {

                inviteRepository.sendEmails(this.invites.filter(i=>i.status=="UNSENT").map(i=>i.id))
                    .then(response =>{
                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Invites Sent',
                            text: 'All unsent Invite successfully sent.',
                        });



                    });

            },
            resendUnconfirmedInvites(){
                inviteRepository.sendEmails(this.invites.filter(i=>i.status=="UNSENT" || i.status=="SENT" || i.status=="VIEWED").map(i=>i.id))
                    .then(response =>{
                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Invites Sent',
                            text: 'All unconfirmed invites have been successfully (re)sent.',
                        });

                        this._updateInvitesList(response.data);

                    });
            },

            _updateInvitesList(newInvites){
                console.log(newInvites);
                //this is probably slow, and could be improved by keeping a map of ids => array index, or using an object instead: data{invites:{1:{},2:{}},...}
                newInvites.forEach(i=>{
                    let id = this.invites.findIndex(invite => invite.id == i.id);
                    this.$set(this.invites[id], 'status', i.status);
                });
            }
        },
        
        mounted() {
            eventRepository.getById(this.id, {
                include: ['client', 'invites', 'questions', 'invites.answers.question', 'days', 'updates', 'days.itinerary_items']
            }).then(response => {
                this.event = response.data;
                this.invites = response.data.invites.map(invite =>{
                    invite.custom_fields.forEach(custom_field=>{
                        invite[custom_field.name] = custom_field.value;
                    });
                    return invite;
                });
                this.updates = response.data.updates;
                this.days = response.data.days;

                //merge custom invite fields
                response.data.client.custom_invite_fields.forEach(field => {
                    this.fields.unshift({
                        key:field.name,
                        sortable:true,
                    });
                    this.csv_fields.unshift(field.name);
                });

                //merge questions into csv fields (not shown in html table)
                response.data.questions.forEach(question=>{
                    this.csv_fields.push(question.text);
                });
                this.loaded = true;
            });
        }
        
    }
</script>

<style scoped>

</style>